// @flow

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { Node } from 'react';

import type { User, Shift } from '../../common/typings';
import SingleTaskFields from '../SingleTaskFields';
import MultipleTasksFields from '../MultipleTasksFields';

import useTaskViews, { BASE_VIEW, TASK_VIEW, TASK_LIST_VIEW } from './useTaskViews';

const EXTRA_PADDING = 30;
const ANIMATION_TIME_MS = 500;
const ANIMATION_TIME_S = 0.5;

const AnimatedContainer = styled.div`
  transition: max-height ${ANIMATION_TIME_S}s ease-out;
  max-height: ${({ maxHeight }) => maxHeight}px;
  width: 100%;
  overflow: hidden;
`;

const ContainerWithVisibility = styled.div`
  visibility: ${props => (props.isScreenVisible ? 'visible' : 'hidden')};
`;

type Props = {
  shift: Shift,
  deviceHeight: number,
  user: User | null,
  renderProp: () => Node,
  baseScreenRef: Object
};

export default function useTaskViewRenderers({
  shift,
  deviceHeight,
  user,
  renderProp,
  baseScreenRef
}: Props) {
  const {
    view,
    currentTask,
    viewTasks: viewTasksCore,
    setTaskView,
    goBack,
    shortenAnimations
  } = useTaskViews(shift.taskInstances || []);

  // Screens are hidden while height animation is happening for better visual effect
  const [isScreenVisible, setIsScreenVisible] = useState(false);

  // Base screen is locked during initial transition, so animation of height can be triggered
  const [isBaseScreenLocked, setIsBaseScreenLocked] = useState(true);

  const [baseScreenHeight, setBaseScreenHeight] = useState(0);
  const [prevScreenHeight, setPrevScreenHeight] = useState(0);

  // Max height is needed for container animation depending on content
  const [maxHeight, setMaxHeight] = useState<number>(0);

  useEffect(() => {
    const { height } = baseScreenRef.current.getBoundingClientRect();
    const originalScreenHeight = height + EXTRA_PADDING;
    setBaseScreenHeight(originalScreenHeight);
    setMaxHeight(originalScreenHeight);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const viewTasks = () => {
    viewTasksCore();
    setTimeout(() => {
      setIsBaseScreenLocked(false);
      setIsScreenVisible(true);
    }, ANIMATION_TIME_MS);
  };

  const onBack = () => {
    const isResetToBaseView =
      view === TASK_LIST_VIEW || (view === TASK_VIEW && shift.taskInstances?.length === 1);
    const goalHeight = isResetToBaseView ? baseScreenHeight : prevScreenHeight;

    const timeNeededByAnimation = goalHeight > maxHeight ? 0 : ANIMATION_TIME_MS;
    setMaxHeight(goalHeight);
    setPrevScreenHeight(baseScreenHeight);
    setIsScreenVisible(false);

    if (isResetToBaseView) {
      setIsBaseScreenLocked(true);
    }
    setTimeout(() => {
      goBack();
      setIsScreenVisible(true);
    }, timeNeededByAnimation);
  };

  const onViewActivated = ref => {
    const { height } = ref.current.getBoundingClientRect();
    setPrevScreenHeight(maxHeight);
    setMaxHeight(height);
  };

  const renderTask = () => {
    if (!currentTask) {
      return null;
    }
    return (
      <ContainerWithVisibility isScreenVisible={isScreenVisible}>
        <SingleTaskFields
          taskInstance={currentTask}
          deviceHeight={deviceHeight}
          user={user}
          onBack={onBack}
          onViewActivated={onViewActivated}
        />
      </ContainerWithVisibility>
    );
  };

  const renderTaskList = () => (
    <ContainerWithVisibility isScreenVisible={isScreenVisible}>
      <MultipleTasksFields
        shift={shift}
        taskPreview={setTaskView}
        deviceHeight={deviceHeight}
        onBack={onBack}
        onViewActivated={onViewActivated}
      />
    </ContainerWithVisibility>
  );

  const isMainContentVisible = view === BASE_VIEW && isBaseScreenLocked;

  const render = () => {
    return (
      <AnimatedContainer maxHeight={maxHeight}>
        {view === TASK_VIEW && renderTask()}
        {view === TASK_LIST_VIEW && renderTaskList()}
        {(view === BASE_VIEW || isBaseScreenLocked) && (
          <ContainerWithVisibility isScreenVisible={isMainContentVisible}>
            {renderProp()}
          </ContainerWithVisibility>
        )}
      </AnimatedContainer>
    );
  };

  return {
    view,
    renderTask,
    renderTaskList,
    render,
    viewTasks,
    shortenAnimations,
    setMaxHeight,
    maxHeight
  };
}
